import { memo, useEffect, useRef, useState } from "react";
import { SmileIdCameraComponent, SpinnerComponent } from "@zenith/components";
import CloseCircle from "@zenith/assets/icons/CloseCircle";
import Camera from "@zenith/assets/icons/Camera";
import Smile from "@zenith/assets/icons/Smile";
import Home from "@zenith/assets/icons/Home";
import Eye from "@zenith/assets/icons/Eye";
import Info from "@zenith/assets/icons/Info";
import StepStatusComponent from "@zenith/features/Tiers/components/StepStatusComponent/step-status-component/step-status-component";
import { SelfieStatusResponseDTO } from "@zenith/services/user";
import { SecurityVerificationStepUpState } from "@zenith/hooks/securityVerificationStepUp.types";
import { formatSecondsAsClock, secondsUntil } from "@zenith/utils/Helpers";

export interface FacialStepUpComponentProps {
  /** State + actions from useSecurityVerificationSelfie (authenticated) or
   * usePublicFacialVerification (public) — either drives this component
   * identically. */
  state: SecurityVerificationStepUpState;
  /** Called once verification succeeds, with the jobId to use as proof on the actual change/reset call. */
  onVerified: (jobId: string) => void;
  /** Discard/back — returns to the form step behind this component. */
  onBack: () => void;
  /**
   * Called when the server says facial isn't applicable (409). Omit to show
   * the built-in "unavailable" screen (the common case, since routing
   * already happened before this component mounted); pass this when the
   * caller wants to fall back to another flow instead (BOAS-7938's OTP
   * fallback for multi-signatory phones, decided at submit since there's
   * no session to route on upfront).
   */
  onNotEligible?: () => void;
}

const FacialStepUpComponent = memo(function FacialStepUpComponent(
  props: FacialStepUpComponentProps
) {
  const { state, onVerified, onBack, onNotEligible } = props;
  const [cameraOpen, setCameraOpen] = useState(false);

  const {
    status,
    jobId,
    attemptsRemaining,
    lockedUntil,
    errorMessage,
    showReassuranceBanner,
    secondsRemaining,
    submit,
    reset,
  } = state;

  const [lockedCountdown, setLockedCountdown] = useState("");

  useEffect(() => {
    if (!lockedUntil) {
      setLockedCountdown("");
      return;
    }
    const tick = () => setLockedCountdown(formatSecondsAsClock(secondsUntil(lockedUntil)));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [lockedUntil]);

  // Guards against re-firing onVerified/onNotEligible. Without this, every
  // call site passes fresh (non-memoized) onVerified/onNotEligible closures
  // on every render; a closure calling setState in the parent causes a
  // re-render, which recreates the closure, which re-runs these effects
  // since the closures are dependencies - even though status/jobId never
  // changed. The result was onVerified (and everything it triggers -
  // encryption, the actual change/reset call) firing repeatedly in a tight
  // loop until something rate-limited it. Track what's already been
  // reported so each verified job / notEligible transition is only ever
  // reported once per occurrence.
  const reportedJobIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (status === "verified" && jobId && reportedJobIdRef.current !== jobId) {
      reportedJobIdRef.current = jobId;
      onVerified(jobId);
    }
    if (status !== "verified") {
      reportedJobIdRef.current = null;
    }
  }, [status, jobId, onVerified]);

  const reportedNotEligibleRef = useRef(false);
  useEffect(() => {
    if (status === "notEligible" && onNotEligible) {
      if (!reportedNotEligibleRef.current) {
        reportedNotEligibleRef.current = true;
        onNotEligible();
      }
    } else {
      reportedNotEligibleRef.current = false;
    }
  }, [status, onNotEligible]);

  const startVerification = () => setCameraOpen(true);
  const tryAgain = () => {
    reset();
    setCameraOpen(true);
  };

  const renderInstructions = () => (
    <>
      <ul className="flex flex-col gap-y-8 text-base-lh-17 font-normal text-neutral-600">
        <li className="flex flex-row">
          <Camera className="mt-1" />
          <p className="ml-3 w-full">
            We need access to your camera for face verification and to
            capture your photo
          </p>
        </li>
        <li className="flex flex-row">
          <Smile className="mt-1" />
          <p className="ml-3 w-full">
            Please, face the camera and keep a straight face
          </p>
        </li>
        <li className="flex flex-row">
          <Home className="mt-1" />
          <p className="ml-3 w-full">Ensure you are in a well lit environment</p>
        </li>
        <li className="flex flex-row">
          <Eye className="mt-1" />
          <p className="ml-3 w-full">
            Remove contacts, glasses and other facial accessories
          </p>
        </li>
      </ul>
      <div className="bg-neutral-info my-12 flex w-full items-start rounded-1.25l p-4">
        <p className="mt-0.5">
          <Info />
        </p>
        <p className="ml-3 text-sm font-normal text-neutral-900">
          Your photograph is safe and will only be used for identification
          purposes.
        </p>
      </div>
    </>
  );

  return (
    <>
      <SmileIdCameraComponent
        open={cameraOpen}
        setOpen={setCameraOpen}
        getPhotos={(photos) => {
          if (photos.length > 0) submit(photos);
        }}
      />

      {status === "locked" ? (
        <div className="mt-6">
          <div className="ml-2 flex flex-row items-center">
            <CloseCircle stroke="#DA291C" transform="scale(1.4)" />
            <h1 className="ml-4 text-2xl font-normal text-neutral-900">
              Verification locked
            </h1>
          </div>
          <p className="ml-10 mt-2 text-sm font-normal text-neutral-600">
            {/* [PO-COPY placeholder — exact lockout copy not yet provided] */}
            You've made too many incorrect verification attempts. Please try
            again in{" "}
            <span className="font-semibold text-primary-300">
              {lockedCountdown}
            </span>
            .
          </p>
          <div className="stepper-buttons-container !mt-20">
            <button
              id="facial-locked-back-button"
              className="secondary-button"
              onClick={onBack}
              type="button"
            >
              Back
            </button>
          </div>
        </div>
      ) : status === "notEligible" && !onNotEligible ? (
        <div className="mt-6">
          <div className="ml-2 flex flex-row items-center">
            <CloseCircle stroke="#DA291C" transform="scale(1.4)" />
            <h1 className="ml-4 text-2xl font-normal text-neutral-900">
              Facial verification unavailable
            </h1>
          </div>
          <p className="ml-10 mt-2 text-sm font-normal text-neutral-600">
            Facial verification isn't available for this account type.
          </p>
          <div className="stepper-buttons-container !mt-20">
            <button
              id="facial-not-eligible-back-button"
              className="secondary-button"
              onClick={onBack}
              type="button"
            >
              Back
            </button>
          </div>
        </div>
      ) : status === "failed" || status === "timedOut" || status === "error" ? (
        <StepStatusComponent
          status={SelfieStatusResponseDTO.status.ERROR_IN_VERIFICATION}
          title="Verification Issue"
          subTitle="Please review the following:"
          icon={<CloseCircle stroke="#DA291C" transform="scale(1.4)" />}
          handleDiscardButton={onBack}
          handleTryAgainButton={tryAgain}
          errorTitle="Verification Problem"
          errorMessage={
            status === "timedOut"
              ? "Verification is taking longer than expected. Please try again."
              : errorMessage || "We could not complete your verification"
          }
          secondErrorMessage={
            typeof attemptsRemaining === "number"
              ? `${attemptsRemaining} attempt${
                  attemptsRemaining === 1 ? "" : "s"
                } remaining`
              : undefined
          }
        />
      ) : status === "submitting" ||
        status === "polling" ||
        status === "verified" ||
        status === "notEligible" ? (
        <div className="mt-20 flex flex-col items-center">
          <SpinnerComponent size={64} type="primary" showIcon={false} />
          <p className="mt-6 text-base font-normal text-neutral-600">
            Verifying your photograph...
          </p>
          <p className="text-sm font-normal text-neutral-400">
            {formatSecondsAsClock(secondsRemaining)}
          </p>
          {showReassuranceBanner && (
            <div className="bg-neutral-info mt-8 flex w-full items-start rounded-1.25l p-4">
              <p className="mt-0.5">
                <Info />
              </p>
              <p className="ml-3 text-sm font-normal text-neutral-900">
                {/* [PO-COPY placeholder — exact 30s reassurance copy not yet provided] */}
                This is taking a little longer than usual. We're still
                verifying your photo — no need to do anything else.
              </p>
            </div>
          )}
        </div>
      ) : (
        <>
          {renderInstructions()}
          <div className="stepper-buttons-container">
            <button
              id="facial-step-up-back-button"
              className="secondary-button"
              onClick={onBack}
              type="button"
            >
              Back
            </button>
            <button
              id="facial-step-up-start-button"
              className="primary-button"
              onClick={startVerification}
              type="button"
            >
              Start Verification
            </button>
          </div>
        </>
      )}
    </>
  );
});

export default FacialStepUpComponent;
